"use strict";
/**
 * @license
 * Copyright 2016 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
export function partitionArray(array, start, end, predicate) {
  while (start < end) {
    const x = array[start];
    if (predicate(x)) {
      ++start;
      continue;
    }
    --end;
    array[start] = array[end];
    array[end] = x;
  }
  return end;
}
export function filterArrayInplace(array, predicate) {
  const length2 = array.length;
  let outIndex = 0;
  for (let i = 0; i < length2; ++i) {
    if (predicate(array[i], i, array)) {
      array[outIndex] = array[i];
      ++outIndex;
    }
  }
  array.length = outIndex;
}
export function maybePadArray(array, newSize) {
  if (array.length === newSize) {
    return array;
  }
  const newArray = new array.constructor(newSize);
  newArray.set(array);
  return newArray;
}
export function getFortranOrderStrides(size, baseStride = 1) {
  const length2 = size.length;
  const strides = new Array(length2);
  let stride = strides[0] = baseStride;
  for (let i = 1; i < length2; ++i) {
    stride *= size[i - 1];
    strides[i] = stride;
  }
  return strides;
}
export function transposeArray2d(array, majorSize, minorSize) {
  const transpose = new array.constructor(array.length);
  for (let i = 0; i < majorSize * minorSize; i += minorSize) {
    for (let j = 0; j < minorSize; j++) {
      const index = i / minorSize;
      transpose[j * majorSize + index] = array[i + j];
    }
  }
  return transpose;
}
export function tile2dArray(array, majorDimension, minorTiles, majorTiles) {
  const minorDimension = array.length / majorDimension;
  const length2 = array.length * minorTiles * majorTiles;
  const result = new array.constructor(length2);
  const minorTileStride = array.length * majorTiles;
  const majorTileStride = majorDimension;
  const minorStride = majorDimension * majorTiles;
  for (let minor = 0; minor < minorDimension; ++minor) {
    for (let major = 0; major < majorDimension; ++major) {
      const inputValue = array[minor * majorDimension + major];
      const baseOffset = minor * minorStride + major;
      for (let minorTile = 0; minorTile < minorTiles; ++minorTile) {
        for (let majorTile = 0; majorTile < majorTiles; ++majorTile) {
          result[minorTile * minorTileStride + majorTile * majorTileStride + baseOffset] = inputValue;
        }
      }
    }
  }
  return result;
}
export function binarySearch(haystack, needle, compare, low = 0, high = haystack.length) {
  while (low < high) {
    const mid = low + high - 1 >> 1;
    const compareResult = compare(needle, haystack[mid]);
    if (compareResult > 0) {
      low = mid + 1;
    } else if (compareResult < 0) {
      high = mid;
    } else {
      return mid;
    }
  }
  return ~low;
}
export function findClosestMatchInSortedArray(haystack, needle, compare, low = 0, high = haystack.length) {
  let bestIndex = -1;
  let bestDistance = Infinity;
  while (low < high) {
    const mid = low + high - 1 >> 1;
    const compareResult = compare(needle, haystack[mid]);
    if (compareResult > 0) {
      low = mid + 1;
    } else if (compareResult < 0) {
      high = mid;
    } else {
      return mid;
    }
    const distance = Math.abs(compareResult);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIndex = mid;
    }
  }
  return bestIndex;
}
export function binarySearchLowerBound(begin, end, predicate) {
  let count = end - begin;
  while (count > 0) {
    const step = Math.floor(count / 2);
    const i = begin + step;
    if (predicate(i)) {
      count = step;
    } else {
      begin = i + 1;
      count -= step + 1;
    }
  }
  return begin;
}
export function findMatchingIndices(input, value) {
  const out = [];
  for (let i = 0, length2 = input.length; i < length2; ++i) {
    if (input[i] === value) {
      out.push(i);
    }
  }
  return out;
}
export function getIndicesComplement(indices, max) {
  const mask = [];
  mask.length = max;
  for (const i of indices) {
    mask[i] = true;
  }
  return findMatchingIndices(mask, void 0);
}
export function arraysEqual(a, b) {
  const length2 = a.length;
  if (b.length !== length2) return false;
  for (let i = 0; i < length2; ++i) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}
export function arraysEqualWithPredicate(a, b, elementsEqual = (a2, b2) => a2 === b2) {
  const length2 = a.length;
  if (b.length !== length2) return false;
  for (let i = 0; i < length2; ++i) {
    if (!elementsEqual(a[i], b[i])) return false;
  }
  return true;
}
export function getInsertPermutation(n, sourceIndex, targetIndex) {
  const newToOld = [];
  if (targetIndex === sourceIndex) {
    for (let i = 0; i < n; ++i) {
      newToOld[i] = i;
    }
    return newToOld;
  }
  newToOld[targetIndex] = sourceIndex;
  for (let oldDim = 0, newDim = 0; oldDim < n; ) {
    if (oldDim === sourceIndex) {
      ++oldDim;
      continue;
    }
    if (newDim === targetIndex) {
      ++newDim;
    }
    newToOld[newDim++] = oldDim++;
  }
  return newToOld;
}
export function scatterUpdate(dest, source, indices) {
  for (let sourceIndex = 0, length2 = indices.length; sourceIndex < length2; ++sourceIndex) {
    const destIndex = indices[sourceIndex];
    if (destIndex === -1) continue;
    dest[destIndex] = source[sourceIndex];
  }
  return dest;
}
export function gatherUpdate(dest, source, indices) {
  for (let destIndex = 0, length2 = indices.length; destIndex < length2; ++destIndex) {
    const sourceIndex = indices[destIndex];
    if (sourceIndex === -1) continue;
    dest[destIndex] = source[sourceIndex];
  }
  return dest;
}
export function transposeNestedArrays(x) {
  const result = [];
  for (let outerIndex = 0, outerLength = x.length; outerIndex < outerLength; ++outerIndex) {
    const inner = x[outerIndex];
    for (let innerIndex = 0, innerLength = inner.length; innerIndex < innerLength; ++innerIndex) {
      let resultInner = result[innerIndex];
      if (resultInner === void 0) {
        resultInner = result[innerIndex] = [];
      }
      resultInner.push(inner[innerIndex]);
    }
  }
  return result;
}
export function spliceArray(array, splices) {
  const parts = [];
  let origOffset = 0;
  for (let i = 0, numSplices = splices.length; i < numSplices; ++i) {
    const { retainCount, deleteCount, insertCount } = splices[i];
    if (retainCount !== 0) {
      parts.push(array.slice(origOffset, origOffset + retainCount));
      origOffset += retainCount;
    }
    origOffset += deleteCount;
    if (insertCount !== 0) {
      parts.push(new Array(insertCount));
    }
  }
  const origLength = array.length;
  if (origOffset !== origLength) {
    parts.push(array.slice(origOffset));
  }
  return new Array(0).concat(...parts);
}
export function getMergeSplices(oldArray, newArray, compare) {
  const splices = [];
  let oldIndex = 0;
  let newIndex = 0;
  const oldCount = oldArray.length;
  const newCount = newArray.length;
  while (oldIndex < oldCount && newIndex < newCount) {
    let c;
    const oldValue = oldArray[oldIndex];
    const newValue = newArray[newIndex];
    c = compare(oldValue, newValue);
    if (c === 0) {
      let retainCount = 1;
      ++oldIndex;
      ++newIndex;
      while (oldIndex < oldCount && newIndex < newCount && (c = compare(oldArray[oldIndex], newArray[newIndex])) === 0) {
        ++retainCount;
        ++oldIndex;
        ++newIndex;
      }
      splices.push({ retainCount, deleteCount: 0, insertCount: 0 });
      continue;
    }
    if (c < 0) {
      let deleteCount = 1;
      while (++oldIndex < oldCount && (c = compare(oldArray[oldIndex], newValue)) < 0) {
        ++deleteCount;
      }
      splices.push({ retainCount: 0, deleteCount, insertCount: 0 });
      continue;
    }
    if (c > 0) {
      let insertCount = 1;
      while (++newIndex < newCount && (c = compare(oldValue, newArray[newIndex])) > 0) {
        ++insertCount;
      }
      splices.push({ retainCount: 0, deleteCount: 0, insertCount });
    }
  }
  if (oldIndex < oldCount || newIndex < newCount) {
    splices.push({
      retainCount: 0,
      deleteCount: oldCount - oldIndex,
      insertCount: newCount - newIndex
    });
  }
  return splices;
}
export function getFixedOrderMergeSplices(oldArray, newArray, equal) {
  const splices = [];
  let oldIndex = 0;
  let newIndex = 0;
  const oldCount = oldArray.length;
  const newCount = newArray.length;
  while (oldIndex < oldCount) {
    let retainCount = 0;
    while (oldIndex < oldCount && newIndex < newCount && equal(oldArray[oldIndex], newArray[newIndex])) {
      ++retainCount;
      ++oldIndex;
      ++newIndex;
    }
    if (retainCount !== 0) {
      splices.push({ retainCount, deleteCount: 0, insertCount: 0 });
    }
    let deleteCount = 0;
    while (oldIndex < oldCount && (newIndex === newCount || !equal(oldArray[oldIndex], newArray[newIndex]))) {
      ++deleteCount;
      ++oldIndex;
    }
    if (deleteCount !== 0) {
      splices.push({ retainCount: 0, deleteCount, insertCount: 0 });
    }
  }
  if (newIndex !== newCount) {
    splices.push({
      retainCount: 0,
      deleteCount: 0,
      insertCount: newCount - newIndex
    });
  }
  return splices;
}
export function mergeSequences(aCount, bCount, compare, aCallback, bCallback, abCallback) {
  let a = 0;
  let b = 0;
  if (aCount !== 0 && bCount !== 0) {
    while (true) {
      const x = compare(a, b);
      if (x < 0) {
        aCallback(a);
        if (++a === aCount) break;
      } else if (x > 0) {
        bCallback(b);
        if (++b === bCount) break;
      } else {
        abCallback(a, b);
        ++a;
        ++b;
        if (a === aCount || b === bCount) break;
      }
    }
  }
  while (a < aCount) {
    aCallback(a);
    ++a;
  }
  while (b < bCount) {
    bCallback(b);
    ++b;
  }
}
export class TypedArrayBuilder {
  data;
  length = 0;
  constructor(cls, initialCapacity = 16) {
    this.data = new cls(initialCapacity);
  }
  resize(newLength) {
    const { data } = this;
    if (newLength > data.length) {
      const newData = new data.constructor(
        Math.max(newLength, data.length * 2)
      );
      newData.set(data.subarray(0, this.length));
      this.data = newData;
    }
    this.length = newLength;
  }
  get view() {
    return this.data.subarray(0, this.length);
  }
  shrinkToFit() {
    this.data = this.data.slice(0, length);
  }
  clear() {
    this.length = 0;
  }
  appendArray(other) {
    const { length: length2 } = this;
    this.resize(length2 + other.length);
    this.data.set(other, length2);
  }
  eraseRange(start, end) {
    this.data.copyWithin(start, end, this.length);
    this.length -= end - start;
  }
}
